import {ComponentStore} from "@ngrx/component-store";
import {SavedReportsPageState} from "./savedReportsPage";
import {Observable, Subscription, withLatestFrom} from 'rxjs';
import {SavedReport} from "../../interfaces/saved-reports";
import {AppState} from "../store";
import {Store} from "@ngrx/store";
import {filter, tap} from 'rxjs/operators';
import {Injectable} from "@angular/core";
import {selectSavedReportComponentObjects, selectSavedReportsInitialized} from '../savedReports/savedReports.selectors';
import {selectFolderComponentObjects} from '../savedReportsFolders/savedReportsFolders.selectors';
import {
  updateSavedReportsStandalone,
} from '../savedReports/savedReports.actions';
import {selectSavedReportsAndFoldersInitialized, selectSavedReportsAndFoldersLoading} from './savedReportsPage.selectors';
import {SavedReportComponentObject, SavedReportComponentObjects} from '../../classes/saved-report-component-object';
import {FolderComponentObject, FolderComponentObjects} from '../../classes/folder-component-object';
import {Actions} from '@ngrx/effects';

@Injectable()
export class SavedReportPageStore extends ComponentStore<SavedReportsPageState> {
  public readonly loading$: Observable<boolean> = this._store.select(selectSavedReportsAndFoldersLoading);
  public readonly savedReportsInitialized$: Observable<boolean> = this._store.select(selectSavedReportsInitialized);

  public readonly reports$: Observable<SavedReportComponentObjects> = this.select((state: SavedReportsPageState) => state.reports);
  public readonly folders$: Observable<FolderComponentObjects> = this.select((state: SavedReportsPageState) => state.folders);
  public readonly breadcrumbs$: Observable<FolderComponentObjects> = this.select((state: SavedReportsPageState) => state.breadcrumbs);
  public readonly currentFolder$: Observable<FolderComponentObject> = this.select((state: SavedReportsPageState) => state.currentFolder);
  public readonly parentFolder$: Observable<FolderComponentObject> = this.select((state: SavedReportsPageState) => state.parentFolder);
  public readonly state$: Observable<SavedReportsPageState> = this.select((state: SavedReportsPageState) => state);
  public readonly initialized$: Observable<boolean> = this.select((state: SavedReportsPageState): boolean => state.initialized);

  private readonly _savedReportFolders$: Observable<FolderComponentObjects> = this._store.select(selectFolderComponentObjects);

  public readonly selectFolder: (folder: FolderComponentObject) => void = this.effect((params$: Observable<FolderComponentObject>): Observable<FolderComponentObject> => params$.pipe(
    tap((folder: FolderComponentObject): void => {
      this._filterReports(folder);
      this._buildBreadCrumb(folder);
    })
  ));

  private readonly _filterReports: (folder: FolderComponentObject) => void = this.effect((params$: Observable<FolderComponentObject>): Observable<FolderComponentObject> => params$.pipe(
    tap((currentFolder: FolderComponentObject): void => this.patchState({
      reports: currentFolder.reports$.value,
      folders: currentFolder.folders$.value
    }))
  ));

  private readonly _buildBreadCrumb: (folder: FolderComponentObject) => void = this.effect((params$: Observable<FolderComponentObject>): Observable<[FolderComponentObject, FolderComponentObjects]> => params$.pipe(
    withLatestFrom(this._savedReportFolders$),
    tap(([folder, savedReportsFolders]: [FolderComponentObject, FolderComponentObjects]): void => {
      const breadcrumbs: FolderComponentObjects = [];
      let searchFolderId: number | null = folder.payload.id;

      while (searchFolderId !== null) {
        const folder: FolderComponentObject = savedReportsFolders.find((folder: FolderComponentObject): boolean => folder.payload.id == searchFolderId);

        breadcrumbs.unshift(folder);
        searchFolderId = folder.payload.attributes.parent_folder_id;
      }

      breadcrumbs.unshift(savedReportsFolders.find((folder: FolderComponentObject): boolean => folder.payload.id === null));

      this.patchState({
        breadcrumbs: breadcrumbs,
        parentFolder: breadcrumbs[breadcrumbs.length - 2],
        currentFolder: breadcrumbs[breadcrumbs.length - 1],
        initialized: true
      });
    })
  ));

  public readonly modeReportToFolder: (params: { report: SavedReportComponentObject, folder: FolderComponentObject }) => void = this.effect((params$: Observable<{ report: SavedReportComponentObject, folder: FolderComponentObject }>): Observable<{ report: SavedReportComponentObject, folder: FolderComponentObject }> => params$.pipe(
    tap((params: { report: SavedReportComponentObject, folder: FolderComponentObject }): void => this._store.dispatch(updateSavedReportsStandalone({
      report: params.report.payload,
      update: {
        saved_reports_folder_id: params.folder.payload.id
      }
    })))
  ));

  public readonly moveReportToParentFolder: (report: SavedReportComponentObject) => void = this.effect((params$: Observable<SavedReportComponentObject>): Observable<[SavedReportComponentObject, SavedReportsPageState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([report, state]: [SavedReportComponentObject, SavedReportsPageState]): void => this._store.dispatch(updateSavedReportsStandalone({
      report: report.payload,
      update: {
        saved_reports_folder_id: state.currentFolder.payload.attributes.parent_folder_id
      } as SavedReport
    })))
  ));

  public readonly folderBack: () => void = this.effect((params$: Observable<void>): Observable<[void, SavedReportsPageState]> => params$.pipe(
    withLatestFrom(this.state$),
    filter(([_, state]: [void, SavedReportsPageState]): boolean => !!state.parentFolder),
    tap(([_, state]: [void, SavedReportsPageState]): void => this.selectFolder(state.parentFolder))
  ));

  private readonly _init$: Subscription = this.effect((): Observable<any> => this._store.select(selectSavedReportsAndFoldersInitialized).pipe(
    withLatestFrom(
      this.state$,
      this._savedReportFolders$
    ),
    filter(([loaded]: [boolean, SavedReportsPageState, FolderComponentObjects]): boolean => loaded),
    tap(([_, state, folders]: [boolean, SavedReportsPageState, FolderComponentObjects]): void => this.selectFolder(folders.find((folder: FolderComponentObject): boolean => folder === state.currentFolder) || folders.find((folder: FolderComponentObject): boolean => folder.payload.id === null)))
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _actions$: Actions
  ) {
    super({
      initialized: false,
      reports: [],
      folders: [],
      breadcrumbs: [],
      currentFolder: null,
      parentFolder: null
    });
  }

}
